<template>
  <div>
    <div id="cn-container" class="cn-controls-navigator">
      <div v-if="error">{{ error }}</div>
      <div v-else-if="isLoading">Loading ...</div>
      <div v-else ref="cnElement">
        <div class="current" v-if="fields && fields?.source && fields?.source?.displayName">
          Currently viewing <strong>{{ fields?.source?.displayName }}</strong>
        </div>
        <div class="buttons">
          <button :class="['button__mapping', { open: isMappingOpen }]" @click="toggleMapping">
            Mappings
          </button>
          <div :class="['mapping-container', { open: isMappingOpen }]">
            <cn-mappings
              :mappings="mappings"
              :selectedMappingElements="selectedMappingElements"
              @setSelectedMappingElement="setSelectedMappingElement"
              @toggleMapping="toggleMapping"
            />
          </div>
          <cn-ig id="cn-ig" :datasource="fields" :ig="ig" @setIg="setIg" />
          <div class="utility-buttons">
            <button
              class="button-safeguards"
              :class="[canShowAll ? 'active' : '', hideUnselectedSafeguards ? '' : 'hideeye']"
              :disabled="!canShowAll"
              @click="toggleHideUnselectedSafeguards($event, false)"
              type="button"
            >
              <span v-if="hideUnselectedSafeguards">Show</span>
              <span v-else>Hide</span>&nbsp;unselected safeguards
            </button>
          </div>
        </div>
        <cn-listing
          id="cn-listing"
          :controls="controls"
          :selectedSafeguards="selectedSafeguards"
          :hideUnselectedSafeguards="hideUnselectedSafeguards"
          :hideUnselectedControlSafeguards="hideUnselectedControlSafeguards"
          :getSafeguardMappings="getSafeguardMappings"
          @toggleSafeguardChecked="toggleSafeguardChecked"
          @toggleHideUnselectedControlSafeguards="toggleHideUnselectedControlSafeguards"
        />
        <div id="export-container" ref="staticExportElement" class="export-container">
          <div class="export-container__text">
            <h3>{{ fields?.exportContainerTitle?.value }}</h3>
            <span>Do you wish to start over?</span>
            <button
              class="export-container__reset"
              :disabled="selectedMappingElements.length === 0 && ig === 0 && !canShowAll"
              @click="
                setSelectedMappingElement(null, true);
                setIg(0);
                backToTop();
              "
            >
              Reset your selection
            </button>
          </div>
          <cn-export id="cn-export" @exportSafeguards="exportSafeguards" />
          <div class="export-container__text mobile">
            <span>Do you wish to start over?</span>
            <button
              class="export-container__reset"
              :disabled="selectedMappingElements.length === 0 && ig === 0 && !canShowAll"
              @click="
                setSelectedMappingElement(null, true);
                setIg(0);
                backToTop();
              "
            >
              Reset your selection
            </button>
          </div>
        </div>

        <div class="utility-buttons bottom">
          <button
            class="button-safeguards"
            :class="[canShowAll ? 'active' : '', hideUnselectedSafeguards ? '' : 'hideeye']"
            :disabled="!canShowAll"
            @click="toggleHideUnselectedSafeguards($event, true)"
            type="button"
          >
            <span v-if="hideUnselectedSafeguards">Show</span>
            <span v-else>Hide</span>&nbsp;unselected safeguards
          </button>
          <button class="button-top" type="button" @click="backToTop($event)">
            Go Back To The Top
          </button>
        </div>

        <div ref="stickyExportElement" class="export-container sticky">
          <div class="inner">
            <div class="export-container__text">
              <h3>{{ fields?.exportContainerTitle?.value }}</h3>
              <span>Do you wish to start over?</span>
              <button
                class="export-container__reset"
                :disabled="selectedMappingElements.length === 0 && ig === 0 && !canShowAll"
                @click="
                  setSelectedMappingElement(null, true);
                  setIg(0);
                  backToTop();
                "
              >
                Reset your selection
              </button>
            </div>
            <cn-export id="cn-export" @exportSafeguards="exportSafeguards" />
            <div class="export-container__text mobile">
              <span>Do you wish to start over?</span>
              <button
                class="export-container__reset"
                :disabled="selectedMappingElements.length === 0 && ig === 0 && !canShowAll"
                @click="
                  setSelectedMappingElement(null, true);
                  setIg(0);
                  backToTop();
                "
              >
                <span>Reset your selection</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, shallowRef } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { ItemQuery } from './CIS-Controls-Navigator.graphql';
import CISControlsNavigatorIG from './CIS-Controls-Navigator-IG.vue';
import CISControlsNavigatorMappings from './CIS-Controls-Navigator-Mappings.vue';
import CISControlsNavigatorListing from './CIS-Controls-Navigator-Listing.vue';
import CISControlsNavigatorExport from './CIS-Controls-Navigator-Export.vue';
import config from '../../../temp/config';

export default {
  name: 'CIS-Controls-Navigator',
  components: {
    CnIg: CISControlsNavigatorIG,
    CnMappings: CISControlsNavigatorMappings,
    CnListing: CISControlsNavigatorListing,
    CnExport: CISControlsNavigatorExport
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  data: function () {
    return {
      ig: 0,
      selectedMappingElements: [],
      hideUnselectedSafeguards: false,
      hideUnselectedControlSafeguards: [],
      isMappingOpen: false,
      scrollState: 0,
      staticScrollPosition: 0
    };
  },
  setup(props) {
    // Use the source 'name' instead of URL because it shows extra characters in the place of a '.'
    // ex. '/content/controls/v8.2' is rendered as '/content/controls/v8,-d-,2'
    const variables = {
      controlsPath: `/sitecore/content/cisecurity/ngen/Content/Controls/${props.fields.source?.name}`,
      mappingsPath: `/sitecore/content/cisecurity/ngen/Content/Controls/${props.fields.source?.name}/Mappings`
    };
    const queryOptions = () => {
      return {
        prefetch: true
      };
    };

    const { result, loading, error, onResult } = useQuery(ItemQuery, variables, queryOptions);

    const selectedSafeguards = reactive([]);
    const totalSafeguards = shallowRef(0);

    onResult((queryResult) => {
      // When query has finished loading and has data, create a list of safeguard IDs
      // so they are all 'selected'.
      if (!queryResult.loading && queryResult.data?.controls?.children?.length > 0) {
        selectedSafeguards.push(
          ...queryResult.data.controls.children.flatMap((c) => c.safeguards).map((s) => s.id)
        );
      }
      totalSafeguards.value = selectedSafeguards.length;
    });

    return {
      result,
      selectedSafeguards,
      totalSafeguards,
      loading,
      error
    };
  },
  mounted() {
    // Creating scroll event listener
    window.addEventListener('scroll', () => {
      this.scrollDetect(this.scrollHome, this.scrollDown, this.scrollUp);
    });
  },
  computed: {
    isLoading() {
      return this.loading || !this.result?.mappings?.children;
    },
    mappings() {
      return this.result?.mappings?.children ?? [];
    },
    controls() {
      if (this.isLoading) return [];

      const output = this.result?.controls?.children ?? [];

      return output;
    },
    canShowAll() {
      // If fewer than all safeguards are selected then return true
      return this.selectedSafeguards.length < this.totalSafeguards;
    }
  },
  methods: {
    getToken() {
      const tokenRequest = new Request(`${config.sitecoreApiHost}/api/fileexport/GetToken`);
      const tokenOptions = {
        method: 'GET'
      };

      let token = undefined;
      return fetch(tokenRequest, tokenOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to get Anti-Forgery token response: status ${response.status}`);
          }

          return response.text();
        })
        .then((text) => {
          const tokenDoc = new DOMParser().parseFromString(text, 'text/html');
          token = tokenDoc?.getElementsByName('__RequestVerificationToken')[0]?.value;

          if (token === undefined || token.length === 0) {
            throw new Error('Failed to find Anti-Forgery token in server response.');
          }

          return token;
        });
    },
    exportSafeguards(onDone, onError) {
      /************************************************************************************
       * NOTE: The fetch in this exportSafeguards function will not work when running on
       * localhost:3000 (connected mode) because the Anti-Forgery cookie generated for the
       * http://localhost:3000 page won't match the Anti-Forgery token returned by the
       * getToken function, which calls the /api/fileexport/GetToken URL on the CM server.
       * The only way to test this function is by deploying it to your local CM server.
       ***********************************************************************************/

      // if onDone or onError are null/undefined then create null functions for them.
      if (!onDone) {
        onDone = () => {};
      }
      if (!onError) {
        onError = () => {};
      }
      if (typeof onDone !== 'function') {
        throw 'onDone parameter must be a function';
      }
      if (typeof onError !== 'function') {
        throw 'onError parameter must be a function';
      }

      this.getToken()
        .then((token) => {
          const request = new Request(
            `${config.sitecoreApiHost}/api/fileexport/ControlsNavigatorToExcel`
          );

          const fileName = `CIS-Controls-Navigator-Export-${new Date()
            .toISOString()
            .slice(0, 19)
            .replaceAll(':', '_')}.xlsx`;

          // Inline method to get the Safeguard data for the export
          // const self = this;
          const getSafeguards = () => {
            const self = this;
            const safeguards = this.controls
              .map((c) =>
                c.safeguards.map((s) => {
                  return { ...s, controlNumber: c?.number };
                })
              )
              .flat()
              .filter((s) => this.selectedSafeguards.includes(s.id))
              .map((s) => {
                // If no Mapping Elements are selected then let the mappings be an empty array.
                const mappings =
                  this.selectedMappingElements.length > 0
                    ? // Get the Mappings/Mapping Elements relavant to this Safeguard
                      self.getSafeguardMappings(s.id).map((m) => {
                        return {
                          Title: m.title?.value,
                          Elements: m.mappingElements.map((e) => e.code?.value || e.title?.value)
                        };
                      })
                    : // Or an empty array
                      [];

                return {
                  Id: s.id,
                  Control: s.controlNumber?.numberValue,
                  Safeguard: s.number?.value,
                  Title: s.title?.value,
                  Description: s.description?.value,
                  IG: s.ig?.numberValue,
                  AssetType: s.assetType?.value,
                  Mappings: mappings
                };
              });

            return safeguards;
          };

          const data = {
            model: {
              Safeguards: getSafeguards(),
              Title: `CIS Critical Security Controls\u00AE ${this.fields.source?.name}`
            }
          };

          const body = JSON.stringify(data);

          const options = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              __RequestVerificationToken: token
            },
            body: body
          };

          fetch(request, options)
            .then((response) => {
              onDone(response);
              const blobPromise = new Promise(function (resolve, reject) {
                response
                  .blob()
                  .then((data) => {
                    resolve({ data: data, statusOk: response.ok });
                  })
                  .catch((err) => {
                    reject(err);
                  });
              });

              return blobPromise;
            })
            .then(({ data, statusOk }) => {
              if (!statusOk) {
                new Response(data)
                  .json()
                  .then((errors) => {
                    if (errors) {
                      const message =
                        errors.find((e) => e.Key === '')?.Value || 'Input data is not valid.';
                      console.error(message, errors);
                      onError(new Error(message));
                    }
                  })
                  .catch((err) => {
                    throw err;
                  });
              } else {
                const href = URL.createObjectURL(data);
                const a = document.createElement('a');
                a.href = href;
                a.setAttribute('download', fileName);
                a.setAttribute('target', '_blank');
                a.click();
                URL.revokeObjectURL(href);
              }
            })
            .catch((err) => {
              onError(err);
            });
        })
        .catch((err) => {
          console.error(err);
          onError(err);
        });
    },
    getSafeguardMappings(safeguardId) {
      const self = this;
      // Inline function to filter the relevant Mapping Elements for the given safeguard.
      // If the selectedMappingElements array isn't empty, then also filter if the Mapping
      // Element's id is in the array.
      const isRelevant = (element, safeguardId) => {
        return element.safeguards?.targetItems?.some((s) => {
          return (
            s.id === safeguardId &&
            (self.selectedMappingElements.length === 0 ||
              self.selectedMappingElements.includes(element.id))
          );
        });
      };

      // Get the Mappings/Mapping Elements relevant to this specific safeguard
      return this.mappings
        .filter((m) =>
          m.mappingElements.some((e) => {
            return isRelevant(e, safeguardId);
          })
        )
        .map((m) => {
          return {
            title: m.title,
            mappingElements: m.mappingElements?.filter((e) => {
              return isRelevant(e, safeguardId);
            })
          };
        });
    },
    setIg(number) {
      if (Number.isNaN(number)) {
        return;
      }
      this.ig = number;

      // Call method to update the selectedSafeguards array
      this.updateSelectedSafeguards();
    },
    updateSelectedSafeguards() {
      // Get the list of Safeguards filtered by the Selected Mapping Elements.
      // Only calculate this if selectedMappingElements is empty.
      const mappingSafeguards =
        this.selectedMappingElements.length === 0
          ? null
          : this.result?.mappings?.children
              ?.filter((mapping) => {
                // If no Mapping Elements are selected then just use all Mappings
                if (this.selectedMappingElements.length === 0) {
                  return true;
                }
                // Test the intersection of selectedMappingElements and the Mapping's
                // Mapping Elements to see if any Mapping Elements are selected
                // in this Mapping.
                const mappingSelectedMEs = [
                  this.selectedMappingElements,
                  mapping.mappingElements.map((me) => me.id)
                ].reduce((a, b) => a.filter((c) => b.includes(c))).length;

                return mappingSelectedMEs > 0;
              })
              .map((mapping) => [
                // Use set to create a list of unique values
                ...new Set(
                  mapping.mappingElements
                    // If Selected Mapping Elements array is empty include all
                    // Mapping Elements otherwise filter by selected elements.
                    .filter(
                      (mappingElement) =>
                        this.selectedMappingElements.length === 0 ||
                        this.selectedMappingElements.includes(mappingElement.id)
                    )
                    // Flatten the safeguards into a single dimension instead of an array of arrays.
                    .flatMap((mappingElement) =>
                      mappingElement.safeguards.targetItems.map((safeguard) => safeguard.id)
                    )
                )
              ]);

      // Get the union of each Mapping's Safeguards
      const mappingSafeguardsUnion =
        mappingSafeguards === null
          ? null
          : mappingSafeguards.reduce((a, b) => [...a, ...b.filter((c) => !a.includes(c))]);

      const updatedSelectedSafeguards = this.result.controls.children
        .flatMap((c) => c.safeguards)
        .filter((s) => {
          return (
            (this.ig === 0 || this.ig >= s.ig.numberValue) &&
            (mappingSafeguardsUnion === null || mappingSafeguardsUnion.some((ms) => ms === s.id))
          );
        })
        .map((s) => s.id);
      // To keep the reactive object, use 'splice' to remove all items and
      // then 'push' to add the needed safeguard IDs.
      this.selectedSafeguards.splice(0, this.selectedSafeguards.length);
      this.selectedSafeguards.push(...updatedSelectedSafeguards);
    },
    toggleHideUnselectedControlSafeguards(controlId) {
      if (this.hideUnselectedControlSafeguards?.includes(controlId)) {
        this.hideUnselectedControlSafeguards = this.hideUnselectedControlSafeguards.filter(
          (s) => controlId !== s
        );
      } else {
        this.hideUnselectedControlSafeguards?.push(controlId);
      }
    },
    toggleSafeguardChecked(safeguard) {
      const currentScroll = this.scrollTop();

      const safeguardIndex = this.selectedSafeguards.indexOf(safeguard.id);
      // Remove or Add safeguard id to array as needed
      if (safeguardIndex >= 0) {
        this.selectedSafeguards.splice(safeguardIndex, 1);
      } else {
        this.selectedSafeguards.push(safeguard.id);
      }

      // Keep the window from scrolling down when safegaurd list refreshes
      this.$nextTick(() => {
        window.scrollTo({ top: currentScroll, behavior: 'instant' });
      });
    },
    toggleHideUnselectedSafeguards(e, scrollToTop) {
      this.hideUnselectedSafeguards = !this.hideUnselectedSafeguards;

      if (this.hideUnselectedSafeguards) {
        this.hideUnselectedControlSafeguards = [];
      }

      if (scrollToTop) this.backToTop();
    },
    setSelectedMappingElement(ids, remove) {
      if (!ids && remove) {
        this.selectedMappingElements = [];
      } else {
        // Convert the ids parameter to an array so it can be used in the forEach loop.
        ids = Array.isArray(ids) ? ids : [ids];

        ids.forEach((id) => {
          if (remove && this.selectedMappingElements.includes(id)) {
            this.selectedMappingElements.splice(this.selectedMappingElements.indexOf(id), 1);
          } else if (!remove && !this.selectedMappingElements.includes(id)) {
            this.selectedMappingElements.push(id);
          }
        });
      }

      this.updateSelectedSafeguards();
    },
    toggleMapping() {
      this.isMappingOpen = !this.isMappingOpen;
    },
    backToTop() {
      // Scroll page to start on cn-container
      if (this.$refs.cnElement) {
        var containerTop = this.$refs.cnElement.offsetTop;
        const bannerElement = document.querySelector('.c-banner');

        if (bannerElement) {
          this.$nextTick(() => {
            window.scrollTo({
              top: containerTop - bannerElement.offsetHeight,
              behavior: 'smooth'
            });
          });
        } else {
          this.$nextTick(() => {
            window.scrollTo({ top: containerTop, behavior: 'smooth' });
          });
        }
      }
    },
    scrollDetect(home, down, up) {
      // Current scroll position
      const currentScroll = this.scrollTop();
      const bottomScrollPosition =
        currentScroll + window.innerHeight - (this.$refs.staticExportElement?.offsetHeight || 0);
      this.staticScrollPosition = this.$refs.staticExportElement?.offsetTop || 0;
      const cnTop =
        (this.$refs.cnElement?.offsetTop || 0) +
        (this.$refs.staticExportElement?.offsetHeight || 0);

      if (this.scrollTop() === 0) {
        home();
      } else if (
        currentScroll > this.scrollState &&
        currentScroll > cnTop &&
        bottomScrollPosition < this.staticScrollPosition
      ) {
        down();
      } else {
        up();
      }

      // Set previous scroll position
      this.scrollState = this.scrollTop();
      this.staticScrollPosition = this.scrollTop();
    },
    // Returns current scroll position
    scrollTop() {
      return window.scrollY;
    },
    // Called when scroll is in initial position
    scrollHome() {},
    // Called when scrolled down
    scrollDown() {
      if (this.$refs.stickyExportElement) {
        this.$refs.stickyExportElement.style.bottom = '0';
      }
    },
    // Called when scolled up
    scrollUp() {
      if (this.$refs.stickyExportElement) {
        this.$refs.stickyExportElement.style.bottom =
          '-' + this.$refs.stickyExportElement.offsetHeight + 'px';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cn-controls-navigator {
  @include padding-x-thin;
  .current {
    text-align: center;
    @include font-styles('Arial', 1.6rem, 200, italic, 2rem, 0, $gray);
    margin-bottom: 2rem;

    @include tablet-up {
      font-size: 2rem;
      text-align: right;
    }
  }
  .buttons {
    display: grid;
    grid-template-rows: auto;
    grid-template-areas:
      'mapping-button'
      'mappings-panel'
      'ig-buttons'
      'utility-buttons';

    @include tablet-up {
      grid-template-columns: 1fr 2fr;
      grid-template-areas:
        'mapping-button ig-buttons'
        'mappings-panel mappings-panel'
        'utility-buttons utility-buttons';
    }

    .utility-buttons {
      grid-area: utility-buttons;
      margin-top: 2.5rem;
      justify-content: center;

      button.button-safeguards {
        margin-bottom: 0;
      }
    }

    @include tablet-up {
      .utility-buttons {
        margin-top: 1rem;
        justify-content: end;
      }
    }
  }

  .button__mapping {
    grid-area: mapping-button;
    position: relative;
    padding: 2.5rem 5rem 2.5rem 2.5rem;
    border: none;
    border-radius: 8px;
    box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.08), 0 3px 8px 0 rgba(0, 0, 0, 0.08);
    background: linear-gradient(
      169deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 59, 92, 1) 20%,
      rgba(0, 134, 191, 1) 100%
    );
    @include font-styles('Arial', 2rem, 500, normal, 2.6rem, 0, $white);
    text-align: left;

    @include tablet-up {
      margin-bottom: 0;
      @include font-styles('Arial', 2.4rem, 500, normal, 3rem, 0, $white);
    }

    @include desktop-up {
      margin-right: 2rem;
    }

    &:before {
      font-size: 2rem;
      content: '∟';
      transform: translate(2rem, -1.67rem) rotate(-45deg);
      color: $white;
      position: absolute;
      top: 50%;
      right: 5rem;
      pointer-events: none;
    }

    &.open {
      &:before {
        transform: translate(2.3rem, -0.67rem) rotate(135deg);
      }
    }
  }

  .mapping-container {
    display: none;
    grid-area: mappings-panel;
    margin-top: -1.5rem;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 17px 18px 0 rgba(0, 0, 0, 0.1), 0 3px 8px 0 rgba(0, 0, 0, 0.1);

    &.open {
      display: block;
    }

    @include tablet-up {
      margin: 2rem 0;
      background-color: $box-gray;
      border-radius: 10px;
      box-shadow: none;
    }
  }

  .export-container {
    padding: 2.4rem 2rem;
    margin-bottom: 4rem;
    background: linear-gradient(
      169deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 59, 92, 1) 20%,
      rgba(0, 134, 191, 1) 100%
    );
    box-shadow: 0 17px 18px 0 rgba(0, 0, 0, 0.08), 0 3px 8px 0 rgba(0, 0, 0, 0.08);
    text-align: center;

    @include tablet-up {
      display: flex;
      justify-content: space-between;
      text-align: left;
      padding: 3rem 4rem 3.8rem 4rem;
      align-items: center;
    }

    &__text {
      h3 {
        @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $white);
        margin-bottom: 3rem;

        @include tablet-up {
          font-size: 2.8rem;
          line-height: 3.5rem;
          margin-bottom: 1rem;
        }
      }

      span {
        display: none;
        @include font-styles('Arial', 1.6rem, 100, normal, 2.4rem, 0, $white);

        @include tablet-up {
          display: inline-block;
          @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $white);
        }
      }

      .export-container__reset {
        display: none;
        width: auto;
        position: relative;
        background: transparent;
        border: none;
        padding-right: 2rem;
        @include font-styles('Arial', 1.8rem, 400, normal, 2.2rem, 0, $white);
        cursor: pointer;

        @include tablet-up {
          display: inline-block;
          font-size: 2rem;
          line-height: 2.6rem;
        }

        &:after {
          content: '';
          position: absolute;
          top: 0.8rem;
          right: 0;
          background-image: url(~@/assets/images/icon-reset-white.svg);
          background-repeat: no-repeat;
          background-size: contain;
          width: 1.5rem;
          height: 1.5rem;
        }

        &:disabled {
          cursor: auto;
          opacity: 0.5;
          font-weight: 200;
        }
      }

      &.mobile {
        margin-top: 2rem;

        @include tablet-up {
          display: none;
        }

        span {
          display: block;
        }

        .export-container__reset {
          display: inline-block;

          &:after {
            content: '';
            top: 0.5rem;
          }

          &:disabled {
            cursor: auto;
            opacity: 0.5;
            font-weight: 200;
          }
        }
      }
    }

    &:deep() {
      button {
        position: relative;
        margin-bottom: 0;

        @include tablet-up {
          width: 22rem;
        }
      }

      .button-preview {
        margin-bottom: 2rem;
      }
    }

    &.sticky {
      position: fixed;
      left: 0;
      bottom: -50rem;
      width: 100%;
      z-index: 1100;
      margin-bottom: 0 !important;
      transition: all 0.5s;

      .inner {
        display: flex;

        justify-content: space-evenly;
        flex-direction: row-reverse;
        width: 100%;
        max-width: 1840px;
        margin: 0 auto;

        @include tablet-up {
          justify-content: space-between;
          flex-direction: row;
          align-items: center;
        }

        h3 {
          display: none;

          @include tablet-up {
            display: block;
          }
        }

        span {
          display: none;

          @include tablet-up {
            display: inline-block;
          }
        }

        &:deep() {
          .button-preview {
            margin-right: 2rem;

            @include tablet-up {
              margin-right: 0;
              margin-bottom: 2rem;
            }

            @include desktop-up {
              margin-bottom: 0;
            }
          }
          .button-export {
            width: auto;
            margin-bottom: 0;

            span {
              display: none;

              @include tablet-up {
                display: inline-block;
              }
            }
          }
        }

        .export-container__text {
          display: none;

          @include tablet-up {
            display: block;
          }

          &.mobile {
            margin-top: 0;
            display: block;

            @include tablet-up {
              display: none;
            }

            .export-container__reset {
              &::after {
                top: -0.5rem;
                width: 2.9rem;
                height: 2.9rem;
              }

              span {
                display: none;

                @include tablet-up {
                  display: inline-block;
                }
              }
            }
          }
        }
      }
    }
  }

  .utility-buttons {
    display: flex;
    justify-content: space-between;

    &.bottom {
      margin-bottom: 4rem;
    }

    @include tablet-up {
      flex-direction: row;
    }

    button {
      background: transparent;
      border: none;
      text-transform: uppercase;
      @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $blue);
      display: inherit;
      margin: 0 auto;

      @include tablet-up {
        margin: initial;
      }

      &.button-safeguards {
        position: relative;
        padding-right: 2.7rem;
        padding-left: 0;
        margin-bottom: 2.8rem;

        @include tablet-up {
          margin-bottom: 0;
        }

        &:disabled {
          opacity: 0.5;
          color: $gray;
        }

        &.active {
          opacity: 1;

          &:after {
            background-image: url(~@/assets/images/icon-eye-open-blue.svg);
            background-repeat: no-repeat;
            background-size: contain;
          }

          &.hideeye {
            &:after {
              background-image: url(~@/assets/images/icon-eye-close-blue.svg);
              background-repeat: no-repeat;
              background-size: contain;
            }
          }
        }

        &:after {
          content: '';
          position: absolute;
          top: 0.3rem;
          right: 0;
          background-image: url(~@/assets/images/icon-eye-close-grey.svg);
          background-repeat: no-repeat;
          background-size: contain;
          width: 2rem;
          height: 2rem;
        }
      }

      &.button-top {
        color: $blue;
        padding: 0;

        @include tablet-up {
          padding-right: 0;
        }
      }
    }
  }
}
</style>
